import { useJobillaOrgId } from '@/hooks'
import {
	campaignsShowQueryKey,
	CampaignWithInternalFormData,
	type ImageData,
	mediaImageIndexQueryKey,
	type UpdateCampaignData,
	useCampaignsShow,
	useCampaignsUpdate,
	useMediaImageUpload,
} from '@jobilla/nexus-sdk'
import { useCallback } from 'react'
import { queryClient } from '../../main'
import {
	ConformedCampaignDetails,
	logger,
	MediaAsset,
	useAlert,
	useCampaignDetails,
	useCampaignDetailsLogoUpload,
} from '../../ui'
import { useConformImageDataToMediaAsset } from '../helpers'

function useConformCampaignDetails() {
	const conformImageDataToMediaAsset = useConformImageDataToMediaAsset()

	return useCallback((campaign: CampaignWithInternalFormData): ConformedCampaignDetails | null => {
		if (!campaign) { return null }

		return {
			title: campaign.title ?? '',
			location: campaign.location ?? '',
			language: campaign.language ?? '',
			requirements: campaign.requirements ?? '',
			invoiceUrl: campaign.invoiceUrl ?? '',
			branding: {
				id: campaign.branding?.id ?? '',
				name: campaign.branding?.name ?? null,
				logoId: campaign.branding?.logoId ?? '',
				logo: campaign.branding?.logo ? conformImageDataToMediaAsset(campaign.branding.logo) : undefined,
				foreground: campaign.branding?.foreground ?? '',
				background: campaign.branding?.background ?? '',
				accentForeground: campaign.branding?.accentForeground ?? campaign.branding?.foreground ?? '',
				accentBackground: campaign.branding?.accentBackground ?? campaign.branding?.background ?? '',
				toneOfVoice: campaign.branding?.toneOfVoice ?? '',
				visualGuidance: campaign.branding?.visualGuidance ?? '',
				website: campaign.branding?.website ?? '',
				bodyFont: campaign.branding?.fonts?.body ?? '',
				titleFont: campaign.branding?.fonts?.title ?? '',
				generateKeyImage: campaign.branding?.generateKeyImage ?? true,
			},
		}
	}, [conformImageDataToMediaAsset])
}

type UseNewCampaignDetailsReturns = {
	isGenerating: boolean
}

export function useNewCampaignDetails(campaignId: string): UseNewCampaignDetailsReturns {
	const jobillaOrgId = useJobillaOrgId()
	const conformImageDataToMediaAsset = useConformImageDataToMediaAsset()
	const alert = useAlert()

	const campaign = useCampaignsShow(campaignId, {
		staleTime: 1000,
	})

	const conformCampaignDetails = useConformCampaignDetails()

	const details = campaign.data ? conformCampaignDetails(campaign.data) : undefined

	const { mutateAsync: campaignMutationAsync, isPending: isSaving } = useCampaignsUpdate({
		mutationKey: ['update-campaign-details'],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	// Logo upload mutation
	const { mutateAsync: logoUploadMutationAsync } = useMediaImageUpload({
		mutationKey: ['campaign-details-logo-upload'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
			// Refetch campaign to get the updated branding with new logo
			void queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onError: (error, variables) => {
			logger.error(error, {
				data: {
					variables,
				},
			})
		},
	})

	const handleDetailsChange = useCallback(async (value: ConformedCampaignDetails) => {
		if (!campaign.data) {
			throw new Error('Campaign not found')
		}

		// Send only the fields this step edits — omitted fields keep their
		// current values server-side, while echoed snapshots can clobber newer data.
		const data: UpdateCampaignData = {
			title: value.title,
			location: value.location,
			language: value.language,
			requirements: value.requirements,
		}

		if (value.branding.id) {
			data.brandingId = value.branding.id
			data.branding = {
				id: value.branding.id,
				name: value.branding.name,
				website: value.branding.website,
				logoId: value.branding.logoId,
				background: value.branding.background || null,
				foreground: value.branding.foreground || null,
				accentBackground: value.branding.accentBackground || null,
				accentForeground: value.branding.accentForeground || null,
				cornerRadius: campaign.data.branding?.cornerRadius ?? null,
				organisationId: campaign.data.branding?.organisationId ?? null,
				toneOfVoice: value.branding.toneOfVoice,
				visualGuidance: value.branding.visualGuidance,
				fonts: {
					body: value.branding.bodyFont,
					title: value.branding.titleFont,
				},
				generateKeyImage: value.branding.generateKeyImage,
			}
		}

		await campaignMutationAsync({ campaign: campaignId, data })
	}, [campaign.data, campaignId, campaignMutationAsync])

	// Logo upload handler
	const handleLogoUpload = useCallback((logo: File, onSuccess: (media: MediaAsset) => void, onFail?: () => void) => {
		const organisationId = jobillaOrgId

		logoUploadMutationAsync({
			organisation: organisationId,
			data: {
				media: logo,
				collection: 'logo',
			},
		}).then((data) => {
			const mediaAsset = conformImageDataToMediaAsset(data as ImageData)

			// Immediately update the UI by calling the success callback
			onSuccess(mediaAsset)
		}).catch((error) => {
			logger.error(error, {
				tags: { component: 'useNewCampaignDetails', action: 'logoUpload' },
			})

			// Undo any optimistic UI updates
			onFail?.()

			// Show error alert to the user
			alert.open(
				'Upload failed',
				'Failed to upload logo. Please try again.',
				'OK',
				() => {},
			)
		})
	}, [logoUploadMutationAsync, jobillaOrgId, conformImageDataToMediaAsset, alert])

	// Set the logo upload callback via the editor hook
	useCampaignDetailsLogoUpload({
		onLogoUpload: handleLogoUpload,
	})

	useCampaignDetails({
		details: details ?? undefined,
		isPending: campaign.isPending,
		isSaving,
		// we don't have a better flag for this right now; it represents the whole campaign, images and all.
		isGenerating: campaign.data?.isGenerated !== true,
		onDetailsChange: handleDetailsChange,
		detailsVersion: campaign.data?.updatedAt,
		generationError: campaign.data?.generationError,
	})

	return {
		isGenerating: campaign.data?.isGenerated !== true,
	}
}
